export type ApiFieldErrors = Record<string, string[]>;

type ApiErrorBody = {
  message?: string;
  errors?: unknown;
};

type GraphQLErrorLike = {
  message?: string;
  extensions?: {
    validation?: unknown;
    errors?: unknown;
  };
};

type ApolloLikeError = {
  message?: string;
  graphQLErrors?: GraphQLErrorLike[];
};

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

export class ApiValidationError extends Error {
  fieldErrors: ApiFieldErrors;
  status?: number;

  constructor(message: string, fieldErrors: ApiFieldErrors = {}, status?: number) {
    super(message);
    this.name = 'ApiValidationError';
    this.fieldErrors = fieldErrors;
    this.status = status;
  }
}

export function normalizeFieldErrors(errors: unknown): ApiFieldErrors {
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return {};
  }

  const normalized: ApiFieldErrors = {};

  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    if (Array.isArray(messages)) {
      const textMessages = messages
        .map((item) => (typeof item === 'string' ? item : String(item ?? '')))
        .filter(Boolean);

      if (textMessages.length > 0) {
        normalized[field] = textMessages;
      }

      continue;
    }

    if (typeof messages === 'string' && messages.length > 0) {
      normalized[field] = [messages];
    }
  }

  return normalized;
}

export function firstFieldMessage(fieldErrors: ApiFieldErrors, field: string): string {
  return fieldErrors[field]?.[0] ?? '';
}

export function flattenFieldErrors(fieldErrors: ApiFieldErrors): Record<string, string> {
  return Object.fromEntries(
    Object.entries(fieldErrors).map(([field, messages]) => [field, messages[0] ?? '']),
  );
}

export function buildValidationError(
  body: unknown,
  fallbackMessage = FALLBACK_MESSAGE,
  status?: number,
): ApiValidationError {
  const parsedBody = (body ?? {}) as ApiErrorBody;
  const fieldErrors = normalizeFieldErrors(parsedBody.errors);
  const firstErrorMessage =
    Object.values(fieldErrors).flat().find(Boolean) ?? parsedBody.message ?? fallbackMessage;

  return new ApiValidationError(firstErrorMessage, fieldErrors, status);
}

export function buildGraphQLValidationError(
  error: unknown,
  fallbackMessage = FALLBACK_MESSAGE,
): ApiValidationError {
  const apolloError = (error ?? {}) as ApolloLikeError;
  const firstGraphQLError = apolloError.graphQLErrors?.[0];
  const fieldErrors = normalizeFieldErrors(
    firstGraphQLError?.extensions?.validation ?? firstGraphQLError?.extensions?.errors,
  );
  const firstErrorMessage =
    Object.values(fieldErrors).flat().find(Boolean) ??
    firstGraphQLError?.message ??
    apolloError.message ??
    fallbackMessage;

  return new ApiValidationError(firstErrorMessage, fieldErrors);
}
